import { clientFor, type PageQuery, type PageResult } from '@yc/api-client'

const api = clientFor('/pod/api')

/** 签收异常级别 */
export type ExceptionLevel = 'info' | 'warn' | 'critical'

/** 签收异常类型：refused → refuse、damaged → damage，其余手工场景用 delay/loss */
export type ExceptionKind = 'delay' | 'damage' | 'loss' | 'refuse'

/** 异常状态：open → handling → closed；open/handling 都算「还没关掉」 */
export type ExceptionStatus = 'open' | 'handling' | 'closed'

/** 可触发的异常动作，与后端 POST /exceptions/:id/<action> 一一对应 */
export type ExceptionAction = 'handling' | 'close'

/** 一条签收异常。字段与 services/pod/internal/exception/model.go 的 Exception 一一对应。 */
export interface Exception {
  id: number
  tenant_id: number
  shipment_id: number
  level: ExceptionLevel
  kind: ExceptionKind
  desc: string
  status: ExceptionStatus
  handled_by: string
  handled_at: string | null
  remark: string
  created_at: string
  updated_at: string
}

/** 列表行：异常 + 所属运单单号（后端子查询带出，不 JOIN） */
export interface ExceptionRow extends Exception {
  shipment_doc_no: string
}

/** 动作入参：只有可选备注（后端 bindOptional 放行空 body） */
export interface ExceptionActionInput {
  remark?: string
}

export interface ExceptionQuery extends PageQuery {
  /** 异常描述或所属运单号模糊匹配 */
  keyword?: string
  status?: string
  level?: string
  kind?: string
  shipment_id?: number
}

/** 与后端 services/pod/internal/exception/model.go 的 LevelNames 同源两份 */
export const LEVEL_LABELS: Record<ExceptionLevel, string> = {
  info: '提示',
  warn: '警告',
  critical: '严重',
}

/** 级别 → 表格里的标签色 */
export const LEVEL_TAG: Record<ExceptionLevel, 'info' | 'warning' | 'danger'> = {
  info: 'info',
  warn: 'warning',
  critical: 'danger',
}

/** 与后端 services/pod/internal/exception/model.go 的 KindNames 同源两份 */
export const KIND_LABELS: Record<ExceptionKind, string> = {
  delay: '延误',
  damage: '破损',
  loss: '丢失',
  refuse: '拒收',
}

/** 与后端 services/pod/internal/exception/model.go 的 StatusNames 同源两份 */
export const STATUS_LABELS: Record<ExceptionStatus, string> = {
  open: '待处理',
  handling: '处理中',
  closed: '已关闭',
}

/** 状态 → 表格里的标签色 */
export const STATUS_TAG: Record<ExceptionStatus, 'info' | 'primary' | 'warning' | 'success' | 'danger'> =
  {
    open: 'danger',
    handling: 'warning',
    closed: 'success',
  }

/**
 * 各状态允许的动作，抄后端 internal/exception/service.go：
 * Handling 挡的是「不在 open」（只有 open 能受理），Close 挡的是「已经关闭」。
 */
const ACTIONS: Record<ExceptionStatus, ExceptionAction[]> = {
  open: ['handling', 'close'],
  handling: ['close'],
  closed: [],
}

/** 状态能不能触发某个动作（最终以后端 409 为准） */
export function canAct(status: ExceptionStatus, action: ExceptionAction): boolean {
  return ACTIONS[status].includes(action)
}

/** 与后端 exception.ActiveStatuses 同源：open / handling 都还没关掉 */
export const ACTIVE_STATUSES: ExceptionStatus[] = ['open', 'handling']

export const listExceptions = (q: ExceptionQuery): Promise<PageResult<ExceptionRow>> =>
  api.get<PageResult<ExceptionRow>>('/exceptions', { ...q })

export const getException = (id: number): Promise<ExceptionRow> =>
  api.get<ExceptionRow>(`/exceptions/${id}`)

/** 动作名 → 端点：受理的状态叫 handling，端点按 spec §3.2 叫 handle（后端另留别名） */
const ACTION_PATH: Record<ExceptionAction, string> = { handling: 'handle', close: 'close' }

const act = (id: number, action: ExceptionAction, input?: ExceptionActionInput): Promise<Exception> =>
  api.post<Exception>(`/exceptions/${id}/${ACTION_PATH[action]}`, input ?? {})

/** 受理：open → handling。端点用 spec §3.2 的 /handle（后端另留 /handling 别名）。 */
export const handlingException = (id: number, input?: ExceptionActionInput): Promise<Exception> =>
  act(id, 'handling', input)

export const closeException = (id: number, input?: ExceptionActionInput): Promise<Exception> =>
  act(id, 'close', input)
